<template>
  <TemplateBase :page="page" class="v-gradient-bg v-gradient-bg--violet">
    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" background-color="bg-app-secondary-7-violet--purple-heart" />
    </template>

    <div class="relative z-0 lg:grid lg:grid-cols-1">
      <section v-if="page.image" class="col-span-full row-start-1 bg-transparent z-0 v-margin-edp-image-to-page-content">
        <MoleculeMaskVisual :image="page.image" />
      </section>

      <div class="lg:hidden md:grid md:grid-cols-12 row-start-2 row-end-3 px-4 sm:px-12 md:px-0 relative mb-6">
        <div class="md:col-start-2 md:col-span-9 font-bold text-white">
          <span v-for="type in page.schoolTypes" :id="type.title" :key="type.title" class="inline-block lg:text-xl xl:text-2xl font-semibold leading-tight mb-3">
            {{ type.title }}
          </span>
          <span v-if="get(page, 'season.title')" class="mx-2 inline-block lg:text-xl xl:text-2xl leading-tight mb-3"> | </span>
          <span class="inline-block lg:text-xl xl:text-2xl leading-tight mb-3">
            {{ get(page, 'season.title') }}
          </span>
          <h1 class="leading-tight text-2xl sm:text-3xl">{{ page.title }}</h1>
        </div>
      </div>

      <section class="education-detail-info pointer-events-none hidden relative z-10 lg:block lg:row-start-2 w-full lg:col-span-full lg:pr-8 xl:pr-24 mx-auto">
        <aside
          class="education-detail-info__data-desktop pointer-events-auto lg:sticky z-10 lg:w-1/3 xl:max-w-md ml-auto rounded-b-2xl p-6 lg:p-5 xl:p-6 sticky-element-desktop shadow-md bg-app-secondary-7-violet--wisteria"
        >
          <MoleculeEducationProductionPageInfoData
            :location="get(page, 'room.title')"
            :price="get(page, 'price')"
            :duration="get(page, 'duration')"
            :is-available="events.length > 0"
            :course-material="page.educationMaterials.length > 0"
            @toggle-modal="toggleModalVisible()"
          />
        </aside>
      </section>

      <!--  separate container with the next section of the page that doesn't belong to the header,
            it contains the body and the mobile event info block and all other page sections -->
      <section class="lg:row-start-2 lg:col-span-full">
        <!--Mobile event info block -->
        <div class="education-detail-info__data-mobile sticky z-20 md:grid md:grid-cols-12 sticky-element-mobile">
          <MoleculeEducationProductionPageInfoData
            :location="get(page, 'room.title')"
            :price="get(page, 'price')"
            :duration="get(page, 'duration')"
            :is-available="events.length > 0"
            class="education-detail-info__data-mobile__content md:col-start-2 md:col-span-10 lg:hidden mb-6 text-white"
            :course-material="page.educationMaterials.length > 0"
            @toggle-modal="toggleModalVisible()"
          />
        </div>

        <MoleculeEducationProductionDetailPageBody
          class="mb-16"
          :title="get(page, 'title')"
          :school-types="get(page, 'schoolTypes')"
          :season-title="get(page, 'season.title')"
          :date="toDate(page.eventDate)"
          :introduction="get(page, 'introduction')"
          :body="get(page, 'description')"
          :organizer="get(page, 'organizer.title')"
          :genres="get(page, 'genres')"
          :part-of-series="page.series"
          :theme="theme"
          :musicians="get(page, 'musicians')"
        >
        </MoleculeEducationProductionDetailPageBody>
      </section>

      <div v-if="page.youtubes.length + page.images.length > 0" class="w-full overflow-hidden lg:row-start-3 mb-12">
        <section class="relative md:grid md:grid-cols-12 lg:max-w-screen-lg lg:mx-auto xl:max-w-screen-xl xl:px-12 lg:gap-x-16">
          <Swiper
            :centered-slides="true"
            :speed="500"
            slides-per-view="auto"
            :loop="false"
            :pagination="{ el: '.slider-pagination', clickable: true, bulletElement: 'button' }"
            :a11y="a11y"
            :modules="[Pagination, A11y]"
            class="carousel-infinite relative mb-0 md:col-span-10 md:col-start-2"
            @swiper="onSwiperInit"
          >
            <SwiperSlide v-for="(video, i) in page.youtubes" :key="`vid${i}`" class="education-detail-videos rounded-[20px] overflow-hidden !w-full">
              <MoleculeCarouselVideo :url="video.oEmbed.url" />
              <div v-if="video.introduction" class="px-6 py-3 text-xs leading-tight p-10 bg-white w-full md:px-10 md:py-10 md:text-base" v-html="video.introduction" />
            </SwiperSlide>
            <SwiperSlide v-for="(image, i) in page.images" :key="`img${i}`" class="rounded-[20px] overflow-hidden !w-full">
              <MoleculePicture v-if="image" :image="image" :lazy="false" />
            </SwiperSlide>
          </Swiper>
        </section>
        <div
          v-if="page.youtubes.length + page.images.length > 1"
          class="slider-navigation slider-navigation--education flex items-center justify-center mt-4 mb-16 relative z-10 xl:mb-20"
        >
          <!-- No template refs on the buttons: under <script setup> a `ref="next"` writes the element into
               the setup binding of the same name, silently replacing the next()/prev() handlers. They were
               leftovers from the Vue 2 version, where slick was driven through $refs. -->
          <button class="order-1" :aria-label="t_('previous')" @click="prev()">
            <AtomIconArrowLeft class="w-4 h-4 text-app-secondary-9-blue--turquoise" />
          </button>
          <!-- Swiper's pagination replaces its target's innerHTML, so it needs its own element here
               rather than the shared wrapper - pointing it at .slider-navigation wiped out the arrows. -->
          <div class="slider-pagination order-2 flex items-center mx-10"></div>
          <button class="order-3" :aria-label="t_('next')" @click="next()">
            <AtomIconArrowRight class="w-4 h-4 text-app-secondary-9-blue--turquoise" />
          </button>
        </div>
      </div>

      <section v-if="page.educationMaterials.length > 0" id="course-material" class="container mx-auto v-page-section-container-padding mb-12 lg:mb-20 xl:mb-32">
        <h2 class="text-xl sm:text-2xl lg:text-3xl xl:text-4xl leading-none font-bold mb-5 xl:mb-8 text-white">{{ t_('material-header') }}</h2>
        <ul class="sm:grid sm:grid-cols-2 gap-x-4 md:hidden">
          <li v-for="item in page.educationMaterials" :key="item.lessonUpId" class="cursor-pointer mb-4" @click="openLessonUp(item.lessonUpId)">
            <MoleculeCardDiscoverPortrait :url="''" :image="item.image" :title="item.subtitle" :subtitle="item.label" :theme="'education'" @click="openLessonUp(item.lessonUpId)" />
          </li>
        </ul>

        <ul class="hidden md:grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          <li v-for="item in page.educationMaterials" :key="item.lessonUpId" class="cursor-pointer" @click="openLessonUp(item.lessonUpId)">
            <MoleculeCardDiscoverLandscape :url="''" :image="item.image" :title="item.subtitle" :subtitle="item.label" :theme="'education'" />
          </li>
        </ul>
      </section>

      <section class="lg:row-start-5">
        <OrganismModal :name="'testModal'" :visible="modalVisible" @close-modal="setModalVisible(false)">
          <div class="text-2xl sm:text-3xl leading-tight pb-6 pr-6 sm:pr-8 sm:pb-8 border-b border-app-primary-grey--concrete text-app-secondary-7-violet--purple-heart">
            <h1 class="font-bold">{{ t_('choose-date-time') }}</h1>
            <h2>{{ page.title }}</h2>
          </div>
          <!-- for each event in production-->
          <ul>
            <li v-for="event in events" :key="event.id" class="py-6 sm:py-8 border-b border-app-primary-grey--concrete">
              <MoleculeEducationProductionOrderInfoRow
                :event-date="event.eventDate"
                :is-education-customer="isEducationCustomer"
                :remaining="event.remaining"
                :education-tix-sale="educationTixSale(event)"
                :url="handlePurchaseUrl(event)"
                :bookable-from-date="educationTixSale(event).bookableFromDate && educationTixSale(event).bookableFromDate.value"
              />
            </li>
          </ul>
        </OrganismModal>
      </section>
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import type { Swiper as SwiperClass } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import { toDate } from '~/assets/util'

const props = defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

// setupTix internally uses Pinia stores / useRuntimeConfig / useRoute - must be called before any await.
const { isEducationCustomer, tixLoginUrl, tixBaseLoginUrl, tixUser } = setupTix()
const educationTixSale = setupPrepareEducationTixSale()
const config = useRuntimeConfig()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const { t_ } = setupTranslationPrefixer('EducationProductionDetail')

// Swiper's A11y module labels and focuses the pagination buttons for us. `{{index}}` is Swiper's own
// placeholder, substituted per bullet, so it's concatenated here rather than kept in the translation -
// vue-i18n would otherwise try to parse the braces as an interpolation of its own.
const a11y = computed(() => ({
  paginationBulletMessage: `${t_('go-to-slide')} {{index}}`,
}))

const events = computed(() => (props.page.events || []).filter((event: CmsData) => new Date(event.eventDate) >= new Date()))
const theme = props.page.production?.theme
const wwwSiteUrl = config.public.wwwSiteUrl

const educationUserEditFormUri = localePath({
  name: 'education-user-edit',
  query: {
    purchaseUrl: route.query?.purchaseUrl,
    returnPath: route.path,
  },
})

const modalVisible = ref(false)
const swiper = ref<SwiperClass | null>(null)

function onSwiperInit(swiperInstance: SwiperClass) {
  swiper.value = swiperInstance
}

function toggleModalVisible() {
  modalVisible.value = !modalVisible.value
}
function setModalVisible(visibility: boolean) {
  modalVisible.value = visibility
}
function next() {
  swiper.value?.slideNext()
}
function prev() {
  swiper.value?.slidePrev()
}
/*
 * Provided by the LessonUp embed script that the CMS loads on this page, never by this app; declared
 * here so the call below type-checks.
 */
declare global {
  interface Window {
    LessonUp: { open: (_id: string) => void }
  }
}

function openLessonUp(id: string) {
  window.LessonUp.open(id)
}
function handlePurchaseUrl(event: CmsData) {
  // if user is not logged in yet
  if (!tixUser.value) {
    // remember the clicked purchaseUrl through a special "educationRedirectPath" for when user is redirected back from Tix
    const educationRedirectPath = event?.educationRedirectPath || null
    if (educationRedirectPath !== null) {
      return `${tixBaseLoginUrl}?returnUrl=${encodeURIComponent(`${wwwSiteUrl}${educationRedirectPath}`)}`
    }

    // event.tixBaseLoginUrl should not be empty, but use the old fallback here just in case
    // should not be used as Tixly is very finicky about query params...
    return `${tixLoginUrl}${encodeURIComponent(`?purchaseUrl=${event.purchaseUrl}`)}`
  }
  // manually go to user update form as a fallback, in case the redirect does not take place
  if (!isEducationCustomer.value) {
    return localePath({
      name: 'education-user-edit',
      query: {
        purchaseUrl: event.purchaseUrl,
        returnPath: route.path,
      },
    })
  }
  return event.purchaseUrl
}

const func = debounce(() => {
  const mobile = document.querySelector('.sticky-element-mobile')
  // Rendered conditionally: on a page without the sticky block there is nothing to position.
  if (!mobile?.parentElement) {
    return
  }
  const mobileDisplay = window.getComputedStyle(mobile).display

  if (mobileDisplay !== 'none') {
    const elementPos = mobile.getBoundingClientRect()
    const parent = mobile.parentElement.getBoundingClientRect()

    if (parent.top < elementPos.top) {
      mobile.classList.add('i-is-stuck-on-mobile')
    } else {
      mobile.classList.remove('i-is-stuck-on-mobile')
    }
  } else {
    const element = document.querySelector('.sticky-element-desktop')
    if (!element?.parentElement) {
      return
    }
    const elementPos = element.getBoundingClientRect()
    const parent = element.parentElement.getBoundingClientRect()

    if (parent.top < elementPos.top) {
      element.classList.add('i-is-stuck-on-desktop')
    } else {
      element.classList.remove('i-is-stuck-on-desktop')
    }
  }
}, 25)

// Make sure that the event listener is also removed, otherwise they will accumulate
onMounted(() => {
  window.addEventListener('scroll', func)
  // This is a hack to have the carousel re-calculate the height of the videos after they're loaded
  setTimeout(() => {
    window.dispatchEvent(new Event('resize'))
  }, 100)
})
onUnmounted(() => window.removeEventListener('scroll', func))

onMounted(() => {
  if (route.query?.showModal === '1') {
    setModalVisible(true)
  }
})

watch(tixUser, (updatedTixUser) => {
  // use redirects if "purchaseUrl" query param is present and user is logged in
  if (route.query?.purchaseUrl !== undefined && updatedTixUser) {
    if (isEducationCustomer.value === true) {
      // forward to "selected" tix purchase url if user has all info
      window.location.href = String(route.query?.purchaseUrl)
    } else {
      // redirect to "user update form" if user is missing school info
      router.push({ path: educationUserEditFormUri })
    }
  }
})
</script>

<style lang="scss">
// scoped scss is not suitable for this component, it won't work in this case because it needs to inherit styles from parent elements

.education-detail-info {
  @screen 2xl {
    padding-left: 8vw;
    padding-right: 8vw;
  }
}

// sticky element class ...... TODO: add more info about how it works....
.education-detail-info__data-mobile {
  @apply top-0 mx-4;

  @screen sm {
    @apply mx-12;
  }

  @screen md {
    @apply mx-0;
  }

  // This is when the info & data block becomes sticky on mobile
  &.i-is-stuck-on-mobile {
    @apply bg-black text-white shadow-xl rounded-b-2xl p-4 mx-0;

    @screen lg {
      @apply hidden;
    }

    .education-detail-info__data-mobile__content {
      @apply flex items-center mb-0;

      @screen sm {
        @apply px-2;
      }

      @screen md {
        @apply col-span-full;
      }
    }
  }
}

.education-detail-info__data-desktop {
  top: theme('spacing.32');

  @screen xl {
    top: theme('spacing.40');
  }

  // This is when the info & data block becomes sticky at desktop
  &.i-is-stuck-on-desktop {
    @apply rounded-2xl shadow-xl;
  }
}

.education-detail-info__data-mobile {
  // This is when the info & data block becomes sticky on mobile
  &.i-is-stuck-on-mobile {
    @apply bg-app-secondary-7-violet--wisteria p-3;

    @screen sm {
      @apply px-8 py-4;
    }
  }
}

.v-margin-edp-image-to-page-content {
  @apply mb-6;

  @screen lg {
    margin-bottom: calc(-1 * theme('spacing.48'));
  }

  @screen xl {
    margin-bottom: calc(-1 * theme('spacing.56'));
  }

  @screen 2xl {
    margin-bottom: calc(-1 * theme('spacing.96'));
  }
}
</style>
